import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Plus, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { IssueVoucherSheet } from './issue-voucher-sheet';
import { VoucherDetailSheet } from './voucher-detail-sheet';
import { voucherState } from './voucher-format';
import { useVoucherProducts, useVoucherSearch } from './vouchers.api';
import { VOUCHER_STATUS, type AdminVoucher, type VoucherSearchModel } from './types';

/**
 * Gift vouchers — every voucher sold or issued, and the tools to fix one.
 *
 * The API leaves unpaid and abandoned checkouts out unless the status filter
 * asks for them, so "Any" here means "any that exist as far as a customer is
 * concerned". The status filter can still reach those for a payment question.
 *
 * The list opens on Active vouchers, the ones staff act on; "Any" is one pick
 * away.
 */

const ANY = 'ANY';
const DEFAULT_STATUS = String(VOUCHER_STATUS.active);

export function ManageVouchersPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [claimed, setClaimed] = useState(ANY);
  const [kind, setKind] = useState(ANY);
  const [productId, setProductId] = useState(ANY);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);

  const products = useVoucherProducts();

  const model: VoucherSearchModel = useMemo(
    () => ({
      searchTerm: query.trim() || null,
      status: status === ANY ? null : Number(status),
      claimed: claimed === ANY ? null : claimed === 'true',
      complimentary: kind === ANY ? null : kind === 'complimentary',
      voucherProductId: productId === ANY ? null : productId,
      page,
      pageSize: size,
    }),
    [query, status, claimed, kind, productId, page, size]
  );

  const search = useVoucherSearch(model);
  const rows = search.data?.vouchers ?? [];
  const total = search.data?.total ?? 0;

  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const filterFields: FilterField[] = [
    {
      id: 'status',
      label: t('vouchers.status'),
      value: status,
      onChange: reset(setStatus),
      // Reset returns to the default, so the default doesn't count as filtered.
      resetValue: DEFAULT_STATUS,
      sort: false,
      options: [
        { value: ANY, label: t('vouchers.any') },
        { value: String(VOUCHER_STATUS.active), label: t('vouchers.statusFilter.active') },
        { value: String(VOUCHER_STATUS.refunded), label: t('vouchers.state.refunded') },
        { value: String(VOUCHER_STATUS.voided), label: t('vouchers.state.voided') },
        { value: String(VOUCHER_STATUS.pendingPayment), label: t('vouchers.state.pendingPayment') },
        { value: String(VOUCHER_STATUS.paymentFailed), label: t('vouchers.state.paymentFailed') },
        { value: String(VOUCHER_STATUS.cancelled), label: t('vouchers.state.cancelled') },
      ],
    },
    {
      id: 'claimed',
      label: t('vouchers.claimedFilter'),
      value: claimed,
      onChange: reset(setClaimed),
      sort: false,
      options: [
        { value: ANY, label: t('vouchers.any') },
        { value: 'true', label: t('vouchers.state.claimed') },
        { value: 'false', label: t('vouchers.state.unclaimed') },
      ],
    },
    {
      id: 'kind',
      label: t('vouchers.kind'),
      value: kind,
      onChange: reset(setKind),
      sort: false,
      options: [
        { value: ANY, label: t('vouchers.any') },
        { value: 'sold', label: t('vouchers.sold') },
        { value: 'complimentary', label: t('vouchers.complimentary') },
      ],
    },
    {
      id: 'product',
      label: t('vouchers.voucher'),
      value: productId,
      onChange: reset(setProductId),
      options: [
        { value: ANY, label: t('vouchers.any') },
        ...(products.data ?? []).map((p) => ({
          value: p.id,
          label: localizedName(p, language),
        })),
      ],
    },
  ];

  const StateBadge = ({ row }: { row: AdminVoucher }) => {
    const state = voucherState(row);
    return <Badge variant={state.tone}>{t(state.key)}</Badge>;
  };

  /** Who has it — the holder, or where it was sent while unclaimed. */
  const HolderCell = ({ row }: { row: AdminVoucher }) =>
    row.holder ? (
      <div className="min-w-0">
        <p className="truncate font-medium">{row.holder.name || '—'}</p>
        <p className="text-muted-foreground truncate text-xs">{row.holder.email}</p>
      </div>
    ) : (
      <div className="min-w-0">
        <p className="text-muted-foreground truncate">
          {row.recipientName || row.recipientEmail || t('vouchers.codeOnly')}
        </p>
        {row.recipientName && row.recipientEmail && (
          <p className="text-muted-foreground truncate text-xs">
            {row.recipientEmail}
          </p>
        )}
      </div>
    );

  const FromCell = ({ row }: { row: AdminVoucher }) =>
    row.buyer ? (
      <p className="truncate">{row.buyer.name}</p>
    ) : (
      <p className="text-muted-foreground truncate">
        {row.isComplimentary ? t('vouchers.complimentary') : t('vouchers.inPerson')}
      </p>
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('vouchers.manage.title')}
        description={t('vouchers.manage.subtitle')}
        actions={
          <Button size="sm" onClick={() => setIssuing(true)}>
            <Plus />
            <span className="hidden sm:inline">{t('vouchers.issue.title')}</span>
          </Button>
        }
      />

      <FilterBar
        busy={search.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('vouchers.manage.searchPlaceholder')}
                aria-label={t('vouchers.manage.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          search.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {t('vouchers.showingOf', { shown: rows.length, total })}
            </p>
          ) : null
        }
      />

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('vouchers.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => void search.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-56" />
              </div>
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          {/* A new search from an empty answer has no rows to dim — the
              spinner goes over the empty state instead. */}
          <BusyOverlay show={search.isFetching} />
          <p
            className={cn(
              'text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm',
              search.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {t('vouchers.manage.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="hidden rounded-xl border lg:block">
            <Table busy={search.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('vouchers.voucher')}</TableHead>
                  <TableHead>{t('vouchers.holder')}</TableHead>
                  <TableHead>{t('vouchers.from')}</TableHead>
                  <TableHead className="text-center">{t('vouchers.uses')}</TableHead>
                  <TableHead>{t('vouchers.expires')}</TableHead>
                  <TableHead className="text-right">{t('vouchers.paid')}</TableHead>
                  <TableHead>{t('vouchers.status')}</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <LocalizedName nameEn={row.nameEn} nameEs={row.nameEs} />
                      <p className="text-muted-foreground font-mono text-xs tracking-wider">
                        {row.code}
                      </p>
                    </TableCell>
                    <TableCell className="max-w-56">
                      <HolderCell row={row} />
                    </TableCell>
                    <TableCell className="max-w-44">
                      <FromCell row={row} />
                    </TableCell>
                    <TableCell className="text-center tabular-nums">
                      {row.usesUsed} / {row.usesTotal}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {row.expiresAt ? formatDate(row.expiresAt) : t('vouchers.never')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.amountPaid - row.amountRefunded)}
                    </TableCell>
                    <TableCell>
                      <StateBadge row={row} />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button size="sm" onClick={() => setDetailId(row.id)}>
                          {t('vouchers.view')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative space-y-2 lg:hidden">
            <BusyOverlay show={search.isFetching} />
            {rows.map((row) => (
              <div
                key={row.id}
                className={cn(
                  'bg-card space-y-3 rounded-xl border p-3',
                  search.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <LocalizedName nameEn={row.nameEn} nameEs={row.nameEs} />
                    <p className="text-muted-foreground font-mono text-xs tracking-wider">
                      {row.code}
                    </p>
                  </div>
                  <StateBadge row={row} />
                </div>
                <HolderCell row={row} />
                <p className="text-muted-foreground text-xs">
                  {t('vouchers.usesOfShort', { used: row.usesUsed, total: row.usesTotal })}{' '}
                  · {t('vouchers.expires')}:{' '}
                  {row.expiresAt ? formatDate(row.expiresAt) : t('vouchers.never')} ·{' '}
                  {formatCurrency(row.amountPaid - row.amountRefunded)}
                </p>
                <Button size="sm" className="w-full" onClick={() => setDetailId(row.id)}>
                  {t('vouchers.view')}
                </Button>
              </div>
            ))}
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <VoucherDetailSheet
        voucherId={detailId}
        open={detailId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailId(null);
        }}
      />

      <IssueVoucherSheet
        open={issuing}
        onOpenChange={setIssuing}
        onIssued={(id) => setDetailId(id)}
      />
    </div>
  );
}
